import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useModal } from '../../contexts/ModalContext';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessProps {
  ctaText?: string;
  steps?: ProcessStep[];
}

const defaultSteps: ProcessStep[] = [
  {
    number: '01',
    title: 'Discovery',
    description: 'We begin with a deep consultation — learning how you live, your vision, your site, and your goals before any design work begins.',
  },
  {
    number: '02',
    title: 'Concept',
    description: 'Initial concepts explore spatial arrangement, form, and materiality. Ideas are developed into clear architectural directions.',
  },
  {
    number: '03',
    title: 'Refinement',
    description: 'The design is refined through collaborative review until every detail aligns with your vision and the site\'s full potential.',
  },
  {
    number: '04',
    title: 'Documentation',
    description: 'Complete architectural drawings, specifications, and permit-ready documentation are prepared for construction.',
  },
  {
    number: '05',
    title: 'Support',
    description: 'We remain engaged throughout construction — ensuring the design intent is realized with precision and care.',
  },
];

export default function Process({ ctaText = 'Start Your Project', steps = defaultSteps }: ProcessProps) {
  const headerRef = useScrollReveal();
  const stepsRef = useScrollReveal();
  const { openModal } = useModal();

  return (
    <section className="py-36 bg-dark-ink relative overflow-hidden">
      {/* Subtle background texture */}
      <div
        className="absolute inset-0 opacity-[0.04] bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/7343db50f28a7f8af28c683caa6aac89.jpg)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-16">
        {/* Header */}
        <div ref={headerRef} className="reveal-on-scroll flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20">
          <div>
            <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">How We Work</p>
            <h2 className="font-serif text-5xl lg:text-6xl font-light text-white leading-tight">The Design Process</h2>
          </div>
          <p className="font-sans text-sm text-white/45 max-w-xs leading-relaxed">
            A structured, transparent process from first consultation to final build — ensuring every decision serves your vision.
          </p>
        </div>

        {/* Steps — horizontal desktop, vertical mobile */}
        <div ref={stepsRef} className="reveal-on-scroll reveal-delay-2">
          {/* Desktop */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Connecting line */}
              <div className="absolute top-[52px] left-[8%] right-[8%] h-px bg-white/10" />
              <div className="grid grid-cols-5 gap-4">
                {steps.map((step, i) => (
                  <div key={i} className="flex flex-col items-center text-center group">
                    {/* Step circle */}
                    <div className="relative mb-8">
                      <div className="w-[104px] h-[104px] rounded-full border border-white/15 bg-dark-ink flex flex-col items-center justify-center group-hover:border-bronze/50 transition-all duration-500 z-10 relative">
                        <span className="font-sans text-[10px] tracking-[0.18em] text-bronze/70 mb-1">{step.number}</span>
                        <span className="font-serif text-base text-white font-light leading-tight px-2 text-center">{step.title}</span>
                      </div>
                      {/* Glow on hover */}
                      <div className="absolute inset-0 rounded-full bg-bronze/5 scale-0 group-hover:scale-100 transition-transform duration-500 blur-xl" />
                    </div>
                    <p className="font-sans text-sm text-white/65 leading-relaxed max-w-[170px] group-hover:text-white/85 transition-colors duration-300">
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="lg:hidden space-y-0">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-6 pb-10">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-sans text-[10px] text-bronze">{step.number}</span>
                  </div>
                  {i < steps.length - 1 && <div className="w-px flex-1 bg-white/10 mt-2" />}
                </div>
                <div className="pb-2">
                  <h4 className="font-serif text-xl font-light text-white mb-2">{step.title}</h4>
                  <p className="font-sans text-sm text-white/60 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={openModal}
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-12 py-4 bg-white text-dark-ink hover:bg-soft-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            {ctaText}
          </button>
        </div>
      </div>
    </section>
  );
}
